import { useCallback, useMemo, useState } from 'react';
import { useFormik, type FormikHelpers } from 'formik';
import type { FormSchema } from '../schema/types';
import { flattenSchemaFields } from '../schema/types';
import { buildInitialValues } from '../engine/initialValuesRegistry';
import { buildValidationConfig } from '../engine/validationRegistry';
import { createValidationSchema } from '../helpers/form';
import { useFieldsValidation } from './useFieldValidation';

export type FormSubmitHelpers<T> = FormikHelpers<T> & {
  resetSubmitted: () => void;
};

type UseFormWithValidationOptions<T extends Record<string, any>> = {
  schema: FormSchema;
  onSubmit: (
    values: T,
    helpers: FormSubmitHelpers<T>,
  ) => void | Promise<void>;
  validateOnMount?: boolean;
  validateOnChange?: boolean;
  validateOnBlur?: boolean;
};

/**
 * Public form-engine hook.
 * Domain passes schema + onSubmit; engine builds initialValues + Yup + Formik.
 */
export function useFormWithValidation<T extends Record<string, any>>({
  schema,
  onSubmit,
  validateOnMount = false,
  validateOnChange = true,
  validateOnBlur = true,
}: UseFormWithValidationOptions<T>) {
  const [isSubmitted, setIsSubmitted] = useState(false);

  const fields = useMemo(() => flattenSchemaFields(schema), [schema]);
  const fieldNames = useMemo(() => fields.map((f) => f.name), [fields]);

  const initialValues = useMemo(
    () => buildInitialValues(fields) as T,
    [fields],
  );

  const validationSchema = useMemo(() => {
    const config = buildValidationConfig(fields);
    return createValidationSchema(config);
  }, [fields]);

  const { requiredFields, isRequired } = useFieldsValidation(
    validationSchema,
    fieldNames,
  );

  const resetSubmitted = useCallback(() => {
    setIsSubmitted(false);
  }, []);

  const form = useFormik<T>({
    initialValues,
    validationSchema,
    onSubmit: async (values, helpers) => {
      try {
        await onSubmit(values, { ...helpers, resetSubmitted });
      } finally {
        helpers.setSubmitting(false);
      }
    },
    validateOnMount,
    validateOnChange,
    validateOnBlur,
    enableReinitialize: true,
  });

  const confirmHandler = useCallback(() => {
    setIsSubmitted(true);
    void form.submitForm();
  }, [form]);

  return {
    form,
    validationSchema,
    requiredFields,
    isRequired,
    isSubmitted,
    setIsSubmitted,
    confirmHandler,
    resetSubmitted,
    schema,
  };
}
